
import { computed, defineComponent, PropType } from 'vue';
import { Instance } from '@/products/api/Instance';
import { BackCommand, SearchViewCommand } from '@/products/api/runtime/CommandExecutor';
import { TranslateFunc } from '@/products/composables/setupComponent';

import Arrow from '@/common/components/Arrow.vue';
import Btn from '@/common/components/Btn.vue';
import Tooltip from '@/common/components/Tooltip.vue';
import { IRouteData } from '@/products/api/runtime/IRouteData';

export default defineComponent({
  components: {
    Btn,
    Arrow,
    Tooltip,
  },

  props: {
    instance: Object as PropType<Instance>,
    t: Function as PropType<TranslateFunc>,
  },

  setup(props) {
    const routeData = computed<IRouteData | undefined>(() => props.instance?.router.routeData);
    const routeHistoryReversed = computed<IRouteData[]>(() =>
      [...(props.instance?.router.history ?? [])].reverse(),
    );

    const lastRouteSearchIndex = computed(() =>
      routeHistoryReversed.value.findIndex(({ view }) => view === 'search'),
    );

    let prevProductNameCache: string | null = null;
    const previousProductName = computed(() => {
      if (lastRouteSearchIndex.value > 1) {
        const currentProductId = routeData.value?.productId;
        const productId = routeHistoryReversed.value.find((route) => {
          if (route.view !== 'detail') {
            return false;
          }
          if (currentProductId && route.productId === currentProductId) {
            return false;
          }
          return true;
        })?.productId;
        if (productId) {
          return (props.instance?.store?.data.productIdToDisplayName || {})[productId];
        }
      }
      return null;
    });

    const previousProductNameCached = computed(() => {
      const prev = previousProductName.value;
      if (!prev) {
        return prevProductNameCache;
      }
      prevProductNameCache = prev;
      return prev;
    });

    const navigateBack = async () => {
      try {
        await props.instance?.execute(new BackCommand());
        prevProductNameCache = null;
      } catch (error) {
        // Ignore Error
      }
    };

    const navigateToSearch = async () => {
      try {
        if (lastRouteSearchIndex.value > 0) {
          await props.instance?.execute(new BackCommand({ step: lastRouteSearchIndex.value }));
        } else {
          await props.instance?.execute(new SearchViewCommand());
        }
        prevProductNameCache = null;
      } catch (error) {
        // Ignore Error
      }
    };

    const allowBack = computed(() => props.instance?.store?.data.itemNotFound !== true);

    return {
      lastRouteSearchIndex,
      previousProductName,
      previousProductNameCached,
      allowBack,
      navigateBack,
      navigateToSearch,
    };
  },
});
